// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CategoryListPageContext } from "@insite/content-library/Pages/CategoryListPage";
import { HomePageContext } from "@insite/content-library/Pages/HomePage";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Button from "@insite/mobius/Button";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryGallery from "@turnerBlueprint/App/Components/Category/CategoryGallery";
import { getDataLayer } from "@turnerBlueprint/XDL/Utilities/DataLayer";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    numberOfCategoriesToDisplay = "numberOfCategoriesToDisplay",
    maximumNumberOfCategoriesPerRow = "maximumNumberOfCategoriesPerRow",
    maximumCategoryImageHeight = "maximumCategoryImageHeight",
    buttonClassName = "buttonClassName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.numberOfCategoriesToDisplay]: string;
        [fields.maximumNumberOfCategoriesPerRow]: string;
        [fields.maximumCategoryImageHeight]: number;
        [fields.buttonClassName]: string;
    };
    cats: number[];
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const categoriesState = getCategoriesDataView(state);
    const shouldLoadCategoriesGallery = !categoriesState.isLoading && !categoriesState.value;

    return {
        categoriesState,
        shouldLoadCategoriesGallery,
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps & HasHistory;

export interface CategoriesGalleryStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
}

export const categoriesGalleryStyles: CategoriesGalleryStyles = {
    titleText: {
        variant: "h3",
        transform: "inherit",
        css: css`
            text-align: left;
            margin-bottom: 0;
        `,
    },
    link: {
        css: css`
            flex-wrap: wrap;
        `,
    },
    imageItem: {
        css: css`
            padding: 10px;
            justify-content: center;
            align-items: flex-end;
            border: 1px solid #f1f1f1;
        `,
    },
    imageContainer: {
        css: css`
            justify-content: flex-start;
            padding-left: 15px;
        `,
    },
    image: {
        css: css`
            img {
                max-height: 100px !important;
            }
        `,
    },
};

function shuffle(categoryList: Category[], maximumCategories: number) {
    const clone: Category[] = [];

    let i = 0;
    let numbers: number[] = [];
    while (i < maximumCategories) {
        const random = Math.floor(Math.random() * categoryList.length);
        if (numbers.includes(random)) {
            continue;
        }
        numbers.push(random);
        clone.push(cloneDeep(categoryList[random]));
        i++;
    }

    numbers = [];

    return clone;
}

const styles = categoriesGalleryStyles;

class CategoriesGallery extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadCategories, shouldLoadCategoriesGallery } = this.props;
        if (shouldLoadCategoriesGallery) {
            loadCategories();
        }
    }

    render() {
        if (this.props.categoriesState.isLoading || !this.props.categoriesState.value) {
            return null;
        }

        const desktop = parseInt(fields.maximumNumberOfCategoriesPerRow, 10);
        const tablet = Math.ceil(desktop / 2);
        const mobile = Math.ceil(tablet / 2);

        const imgProps = {
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: `${fields.maximumCategoryImageHeight}px`,
            },
        };

        const onClick = () => {
            // Xng-Change: TSC-177 Shop All Manufacturers Event
            const dataLayer = getDataLayer();
            if (dataLayer) {
                const googleAnalyticsData = {
                    event: "shop_by_category",
                    text: translate("View All Categories"),
                    destination: "/Catalog",
                };
                dataLayer.push(googleAnalyticsData);
            }

            this.props.history.push("/Catalog");
        };

        const marginStyles = {
            css: css`
                > div:nth-child(${fields.maximumNumberOfCategoriesPerRow}n) {
                    margin-right: 0;
                }
            `,
        };

        return (
            <StyledWrapper {...styles.container} data-test-selector="categoriesGallery">
                <FlexWrapContainer style={{ marginBottom: "10px" }}>
                    <FlexItem flexColumns={2} style={{ alignItems: "center" }}>
                        {this.props.fields.title && (
                            <Typography
                                variant="headerSecondary"
                                {...styles.titleText}
                                data-test-selector="categoriesGalleryTitle"
                                size={17}
                                weight={800}
                                transform="uppercase"
                            >
                                {this.props.fields.title}
                            </Typography>
                        )}
                    </FlexItem>
                    <FlexItem flexColumns={2} style={{ justifyContent: "flex-end" }}>
                        <Button
                            variant="primary"
                            onClick={() => onClick()}
                            // XNG-Change TSC-173 adding className to the inner typography in favor for GA4
                            {...{ typographyProps: { className: fields.buttonClassName } as any }}
                            className={this.props.fields.buttonClassName}
                        >
                            <Typography transform="uppercase" as="span" weight={700} size={15}>
                                {translate("View All Categories")}
                            </Typography>
                        </Button>
                    </FlexItem>
                </FlexWrapContainer>
                {!this.props.categoriesState.isLoading && this.props.categoriesState.value!.length > 0 && (
                    <CategoryGallery
                        categories={this.props.categoriesState.value}
                        maxCategoriesPerRow={fields.maximumNumberOfCategoriesPerRow}
                        maxCategoryImageHeight={fields.maximumCategoryImageHeight}
                        maxNumberofCategoriesPerRow={fields.maximumNumberOfCategoriesPerRow}
                        onClick={onClick}
                    ></CategoryGallery>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CategoriesGallery)),
    definition: {
        group: "Categories",
        icon: "Image",
        displayName: "Category Gallery",
        allowedContexts: [CategoryListPageContext, HomePageContext],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.maximumCategoryImageHeight,
                displayName: "Maximum category image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "General",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of categories to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
            {
                name: fields.buttonClassName,
                displayName: "Button Class Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
